import { Button } from 'platform/components';
import { useState } from 'react';
import { useFilterCategorySelection } from '../../../../hooks/useFilterCategorySelection';
import type { EagColumnOrGroup } from '../../../../types/EagColumnGroup';
import type { FilterState } from '../../../../types/FilterState';
import { FilterContentArea } from '../FilterContentArea/FilterContentArea';
import { FilterCategoryMenu } from './components/FilterCategoryMenu/FilterCategoryMenu';
type FilterPanelProps = {
  columnConfig: EagColumnOrGroup[];
  filterState: FilterState;
  onFilterChange: (key: string, value: unknown) => void;
  onResetFilters: () => void;
};

/**
 * Main filter panel for the desktop 75vw slide-in filter UI.
 *
 * Layout:
 * ```
 * ┌──────────────────────────────────────────────────────┐
 * │ ┌──────────────────────┬──────────────────────────┐  │
 * │ │ [Search...]          │  Content (scrollable)    │  │
 * │ │ Category list        │  - 2-column grid         │  │
 * │ │  ● General      (2)  │                          │  │
 * │ │  ● Pricing           │                          │  │
 * │ │                      │                          │  │
 * │ │ [Reset filters]      │                          │  │
 * │ └──────────────────────┴──────────────────────────┘  │
 * └──────────────────────────────────────────────────────┘
 * ```
 *
 * The left column uses a custom searchable category list (`FilterCategoryMenu`)
 * that supports cross-category search — typing a filter's name will surface the
 * parent category even if the category label itself doesn't match the query.
 *
 * ## State
 * - `selectedCategoryId` — managed by `useFilterCategorySelection`, auto-set to
 *   first visible category on mount and re-corrected when search filters it out
 * - `searchTerm` — controlled by this component, forwarded to both the category
 *   menu (for filtering the category list) and the content area (for filtering
 *   the filter widgets within the selected category)
 *
 * ## Why props, not store reads
 *
 * `filterState`, `onFilterChange`, and `onResetFilters` are stable callbacks
 * injected from `EagGridTable` via `Sidebar`.  This keeps the panel
 * decoupled from the store and trivially testable without a provider.
 */
export function FilterPanel(props: FilterPanelProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const {
    selectedCategoryId,
    setSelectedCategoryId
  } = useFilterCategorySelection({
    columnConfig: props.columnConfig,
    filterState: props.filterState,
    searchTerm
  });
  return <div className="flex h-full overflow-hidden">
      {/* Left: custom searchable category list + Reset button below */}
      <div className="w-56 flex-shrink-0 border-r border-gray-100 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-hidden">
          <FilterCategoryMenu columnConfig={props.columnConfig} filterState={props.filterState} selectedCategoryId={selectedCategoryId} onCategorySelect={setSelectedCategoryId} searchTerm={searchTerm} onSearchChange={setSearchTerm} />
        </div>
        <div className="flex-shrink-0 px-4 py-4 border-t border-gray-100">
          <Button variant="secondary" isFullWidth onClick={props.onResetFilters} title="Reset filters" />
        </div>
      </div>

      {/* Right: 2-column filter content area, narrowed by searchTerm */}
      <div className="flex-1 overflow-hidden">
        <FilterContentArea columnConfig={props.columnConfig} selectedCategoryId={selectedCategoryId} filterState={props.filterState} onFilterChange={props.onFilterChange} searchTerm={searchTerm} />
      </div>
    </div>;
}